import { useCallback } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { MaePaiUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/mae-pai-use-case'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { showToast } from '@/infra/sonner'
import type { TCliente } from '@/types/cliente'

type TUseDadosMaePaiProps = {
  refetch: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  onNext: () => void
  compradores: TCompradoresCadastrados
  nacionalidadePrincipalId: string
}

export function useDadosMaePai({ onNext, compradores, refetch, nacionalidadePrincipalId }: TUseDadosMaePaiProps) {
  const { trigger, setValue, reset, watch, setError } = useFormContext<CompradorPessoaFisicaStepSchemaInput>()
  const compradorMenor = watch('dados')
  const cpfMaePai = watch('maePai.dados.cpf') ?? ''
  const buscouCpf = watch('maePai.dados.buscouCpf')
  const emailsJaVinculados = watch('emailsJaVinculados')

  const cpfRaw = cpfMaePai?.replaceAll(/\D/g, '')
  const useCase = new MaePaiUseCase(compradores, compradorMenor, emailsJaVinculados, nacionalidadePrincipalId)

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)

    if (!ok && error === 'MISSING_CPF') {
      trigger('maePai.dados.cpf')
      return
    }

    if (!ok && error === 'NEED_SEARCH') {
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
      })
      return
    }

    if (await trigger('maePai.dados')) onNext()
  }, [cpfMaePai, buscouCpf, trigger, onNext])

  const handleBuscarCliente = useCallback(async () => {
    const validacaoCpf = useCase.validarCpfMaePai(cpfRaw)

    if (!validacaoCpf.ok) {
      const errorMessage = {
        SAME_AS_COMPRADOR: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_mae_pai_comprador'),
        MAE_PAI_ALREADY_LINKED: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_ja_vinculado'),
      }
      setError('maePai.dados.cpf', { message: errorMessage[validacaoCpf.reason] })
      return
    }

    const result = await refetch()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result)
    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })

        setValue('maePai', fetchResult.patch)
        break
      case 'NOT_FOUND':
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('maePai.dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({
          variant: 'error',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.erro'),
        })
        break
    }
  }, [cpfRaw])

  const handleLimpar = useCallback(() => {
    setValue('maePai', useCase.limparFormulario(), { shouldValidate: false })
  }, [reset])

  return {
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
    },
  }
}
